import { IArcGisGroup } from "@mctypes/global.types";
import { Alert, Button, Card, Group, List, Loader, Stack, Text, ThemeIcon, Title } from "@mantine/core";
import { IconCheck, IconX } from "@tabler/icons-react";
import React, { useEffect, useState } from "react";
import { useAppSelector } from "../../../../hooks";
import { DimensionEnums } from "../../../../mc-shared/sharedConstants";
import { doGetSingleArcgisGroup } from "../../gis/GisAPI";
import ConnectArcgisModal from "./ConnectArcgisModal";
import UpdateDefaultArcgisMap from "./UpdateDefaultArcgisMap";

export const ConnectedArcgisProject = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [arcgisGroup, setArcGisGroup] = useState<IArcGisGroup>(null);
  const [connectionFailed, setConnectionFailed] = useState<boolean>(null);
  const [loading, setLoading] = React.useState<boolean>(false);

  const project = useAppSelector((state) => state.adminReducer.project);
  const arcgis = project?.arcgis;

  useEffect(() => {
    init();
  }, [arcgis]);

  const init = async () => {
    try {
      if (arcgis.groupId != null) {
        setLoading(true);
        const arcgisGroup = await doGetSingleArcgisGroup(project?._id, arcgis?.groupId);
        setArcGisGroup(arcgisGroup);
        setConnectionFailed(false);
      }
    } catch (err) {
      setConnectionFailed(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card withBorder radius="md" p="lg">
      <Stack gap="md">
        <Title order={3}>Tilkoblet Multiconsult Cloud-GIS gruppe</Title>
        {loading && <Loader size="sm" />}

        {!loading && (
          <Stack gap="md">
            {arcgis?.groupId != null && (
              <>
                <Group gap="xs">
                  <Text fw={600}>Status ArcGIS Tilkobling:</Text>
                  <StatusIcon isValid={connectionFailed === false} />
                </Group>
                {connectionFailed && (
                  <Alert color="red">
                    Kunne ikke koble til gruppe på Multiconsult Cloud GIS konto. Sikker på at du har angitt riktig
                    gruppetid? Kontakt support for mer informasjon.
                  </Alert>
                )}
              </>
            )}
            <Button variant="default" onClick={() => setModalOpen(true)}>
              Koble Multiconsult Cloud GIS Gruppe
            </Button>
            {modalOpen && <ConnectArcgisModal toggle={setModalOpen} />}
            {arcgisGroup != null && (
              <Stack gap="lg">
                <ArcGisGroup arcgisGroup={arcgisGroup} />
                <Stack gap="xs">
                  <Title order={5}>Default 2d-kart</Title>
                  {arcgisGroup.maps.find((map) => map.type === "Web Map") != null ? (
                    <UpdateDefaultArcgisMap
                      defaultMapId={arcgis.default2dMap}
                      arcgisMaps={arcgisGroup.maps.filter((map) => map.type === "Web Map")}
                      projectId={project._id}
                      dimension={DimensionEnums.TWODIMENSION}
                    />
                  ) : (
                    <Text size="sm" fs="italic">
                      Denne gruppen inneholder ingen 2d-kart
                    </Text>
                  )}
                </Stack>
                <Stack gap="xs">
                  <Title order={5}>Default 3d-kart</Title>
                  {arcgisGroup.maps.find((map) => map.type === "Web Scene") != null ? (
                    <UpdateDefaultArcgisMap
                      defaultMapId={arcgis.default3dMap}
                      arcgisMaps={arcgisGroup.maps.filter((map) => map.type === "Web Scene")}
                      projectId={project._id}
                      dimension={DimensionEnums.THEREEDIMENSION}
                    />
                  ) : (
                    <Text size="sm" fs="italic">
                      Denne gruppen inneholder ingen 3d-kart
                    </Text>
                  )}
                </Stack>
              </Stack>
            )}
          </Stack>
        )}
      </Stack>
    </Card>
  );
};

export default ConnectedArcgisProject;

const ArcGisGroup: React.FC<{ arcgisGroup: IArcGisGroup }> = ({ arcgisGroup }) => {
  return (
    <Stack gap="xs">
      <Stack gap={2}>
        <Text size="sm" fw={600}>
          Tilkoblet gruppe
        </Text>
        <Text>{arcgisGroup.title}</Text>
      </Stack>
      <Title order={5}>Maps</Title>
      <List>{arcgisGroup && arcgisGroup.maps.map((map) => <List.Item key={map.id}>{map.title}</List.Item>)}</List>
      {arcgisGroup?.maps?.length === 0 && <Alert color="blue">Ingen kart ble funnet på denne gruppen</Alert>}
    </Stack>
  );
};

const StatusIcon: React.FC<{ isValid: boolean }> = ({ isValid }) => (
  <ThemeIcon color={isValid ? "green" : "red"} variant="light" size="sm" radius="xl">
    {isValid ? <IconCheck size={14} /> : <IconX size={14} />}
  </ThemeIcon>
);
